import { EyeIcon, LogOutIcon, MonitorIcon, MoonIcon, SunIcon, Undo2Icon } from "lucide-react";
import { useState } from "react";
import type { Me } from "@/api/me";
import { useSignOut } from "@/auth/AuthGate";
import { stopViewAs } from "@/auth/view-as";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/primitives";
import { useTheme, type ThemePreference } from "@/lib/theme";
import { initials } from "@/lib/utils";
import { ViewAsDialog } from "./ViewAsDialog";

export function UserMenu({ me }: { me: Me }) {
  const { theme, setTheme } = useTheme();
  const signOut = useSignOut();
  const [viewAsOpen, setViewAsOpen] = useState(false);

  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Account menu"
          className="rounded-full outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
        >
          <Avatar className="size-8">
            <AvatarFallback>{initials(me.display_name)}</AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <div className="flex items-center gap-3 px-2 py-2">
          <Avatar className="size-9">
            <AvatarFallback>{initials(me.display_name)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{me.display_name}</p>
            <p className="truncate text-xs text-muted-foreground">{me.email}</p>
          </div>
        </div>
        {me.roles.length > 0 && (
          <p className="truncate px-2 pb-2 text-xs text-muted-foreground">{me.roles.join(" · ")}</p>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Theme</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={theme} onValueChange={(v) => setTheme(v as ThemePreference)}>
          <DropdownMenuRadioItem value="light" onSelect={(e) => e.preventDefault()}>
            <SunIcon /> Light
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark" onSelect={(e) => e.preventDefault()}>
            <MoonIcon /> Dark
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="system" onSelect={(e) => e.preventDefault()}>
            <MonitorIcon /> System
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        {me.can_view_as && (
          <>
            <DropdownMenuSeparator />
            {me.view_as ? (
              <DropdownMenuItem onSelect={stopViewAs}>
                <Undo2Icon /> Back to my access
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onSelect={() => setViewAsOpen(true)}>
                <EyeIcon /> View as another user…
              </DropdownMenuItem>
            )}
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={signOut}>
          <LogOutIcon /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    {me.can_view_as && !me.view_as && <ViewAsDialog open={viewAsOpen} onOpenChange={setViewAsOpen} />}
    </>
  );
}
